import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const DinoGame = () => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const requestRef = useRef();

  const [score, setScore] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  // Using refs for mutable game state to avoid stale closures in animation loop
  const gameState = useRef({
    dino: { x: 50, y: 140, width: 40, height: 40, dy: 0, grounded: false }, // Adjusted y/size for emoji
    obstacles: [],
    frame: 0,
    score: 0,
    isRunning: false
  });

  const resetGame = useCallback(() => {
    gameState.current = {
      dino: { x: 50, y: 140, width: 40, height: 40, dy: 0, grounded: false },
      obstacles: [],
      frame: 0,
      score: 0,
      isRunning: true
    };
    setScore(0);
    setIsGameOver(false);
    setGameStarted(true);
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const state = gameState.current;

    // Constants
    const GRAVITY = 0.6;
    const SPEED = 5;
    const CACTUS_SPAWN_RATE = 100;
    const GROUND_Y = 180;

    if (state.isRunning) {
        // Dino Physics
        if (!state.dino.grounded) {
            state.dino.dy += GRAVITY;
            state.dino.y += state.dino.dy;
        }

        // Ground Collision
        if (state.dino.y >= 140) { // 180 - 40 height
            state.dino.y = 140;
            state.dino.dy = 0;
            state.dino.grounded = true;
        } else {
            state.dino.grounded = false;
        }

        // Spawn Obstacles
        if (state.frame % CACTUS_SPAWN_RATE === 0) {
            // Randomize height slightly (rocks/cones)
            // Keep width fixed for emojis mostly
            state.obstacles.push({
                x: canvas.width,
                y: 150, // Fixed ground pos roughly
                width: 30,
                height: 30,
                type: Math.random() > 0.5 ? '🚧' : '🪨'
            });
        }

        // Move Obstacles
        state.obstacles.forEach(obs => {
            obs.x -= SPEED;
        });

        // Cleanup Obstacles
        state.obstacles = state.obstacles.filter(obs => obs.x + obs.width > 0);

        // Collision Detection (Hitbox slightly smaller than emoji visual)
        state.obstacles.forEach(obs => {
            if (
                state.dino.x < obs.x + obs.width - 5 &&
                state.dino.x + state.dino.width - 5 > obs.x &&
                state.dino.y < obs.y + obs.height &&
                state.dino.y + state.dino.height > obs.y
            ) {
                state.isRunning = false;
                setIsGameOver(true);
            }
        });

        // Score
        if (state.frame % 10 === 0) {
            state.score++;
            setScore(state.score);
        }

        state.frame++;
    }

    // --- DRAWING ---
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Background (Sky)
    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
    gradient.addColorStop(0, '#E0F2FE'); // Sky blue light
    gradient.addColorStop(1, '#FFF');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Ground
    ctx.beginPath();
    ctx.moveTo(0, GROUND_Y);
    ctx.lineTo(canvas.width, GROUND_Y);
    ctx.strokeStyle = '#64748B';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Dino (Emoji)
    ctx.save(); // Save context to flip
    ctx.scale(-1, 1); // Flip horizontally
    ctx.font = '40px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    // Since we flipped context, x coordinate needs to be inverted.
    // The emoji is drawn at -x. To keep it at state.dino.x, we draw at -(state.dino.x + width) roughly
    // Actually simpler: translate to center of dino, scale, then draw at 0,0 relative

    // Reset transform logic for simpler approach:
    ctx.restore();

    // Proper Flip:
    ctx.save();
    ctx.translate(state.dino.x + state.dino.width / 2, state.dino.y + state.dino.height / 2);
    ctx.scale(-1, 1);
    ctx.font = '40px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🏃', 0, 0);
    ctx.restore();

    // Obstacles
    state.obstacles.forEach(obs => {
        ctx.font = '30px Arial';
        ctx.fillText(obs.type, obs.x, obs.y);
    });

    // Draw score on canvas
    ctx.fillStyle = '#333';
    ctx.font = 'bold 20px monospace';
    ctx.textAlign = 'right';
    ctx.fillText(`Score: ${state.score}`, canvas.width - 20, 30);

    requestRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [animate]);

  const handleJump = useCallback((e) => {
    // Prevent default actions for Touch and Space to avoid scrolling
    if (e.cancelable && (e.type === 'touchstart' || e.code === 'Space')) {
        e.preventDefault();
    }

    const state = gameState.current;

    // Start/Restart Logic
    if ((!gameStarted || isGameOver) && (e.code === 'Space' || e.type === 'click' || e.type === 'touchstart')) {
        resetGame();
        return;
    }

    // Jump Logic
    if (state.isRunning && state.dino.grounded && (e.code === 'Space' || e.code === 'ArrowUp' || e.type === 'click' || e.type === 'touchstart')) {
        state.dino.dy = -12; // Jump Force
        state.dino.grounded = false;
    }
  }, [gameStarted, isGameOver, resetGame]);

  useEffect(() => {
    window.addEventListener('keydown', handleJump);
    return () => window.removeEventListener('keydown', handleJump);
  }, [handleJump]);

  return (
    <div className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-200 select-none outline-none relative overflow-hidden" tabIndex="0">
      <h3 className="text-xl font-bold mb-2 text-indigo-900 z-10">{t('game.generating')}</h3>
      <p className="mb-4 text-gray-600 text-sm z-10">{t('game.instruction')}</p>

      <div className="relative w-full max-w-[600px]">
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            onClick={handleJump}
            onTouchStart={handleJump}
            className="bg-white border-2 border-gray-100 rounded-lg shadow-inner cursor-pointer block w-full h-auto touch-manipulation"
          />

          {/* Overlays */}
          {!gameStarted && !isGameOver && (
               <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-lg pointer-events-none">
                   <div className="bg-white/90 px-6 py-3 rounded-full shadow-lg font-bold text-blue-600 animate-pulse">
                       {t('game.start')}
                   </div>
               </div>
          )}

          {isGameOver && (
               <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg pointer-events-none">
                   <div className="bg-white px-6 py-4 rounded-xl shadow-xl text-center transform scale-110">
                       <div className="text-red-600 font-black text-xl mb-1">{t('game.game_over')}</div>
                       <div className="text-gray-600 font-bold">{t('game.score')}: {score}</div>
                       <div className="text-blue-500 text-sm mt-2 font-bold animate-pulse">{t('game.retry')}</div>
                   </div>
               </div>
          )}
      </div>

      <div className="mt-4 text-gray-400 text-xs text-center">
         {t('game.controls')}
      </div>
    </div>
  );
};

export default DinoGame;
